import { GETEnvironmentsResponseParams } from 'api/types'
import { PROJECTS_CREDENTIALS, PROJECTS_DEFAULTS } from 'constants/route'
import { initialValues as emptyInitialValues } from 'modules/Projects/CreateEnvironment/CreateEnvironmentForm'
import {
  alphaNumericMildCharsWithLeadEndSpacesRegEx,
  warehouseValuesSchema
} from 'modules/Projects/CreateProject/CreateProjectForm'
import { EditEnvironmentFormikValueTypes } from 'modules/Projects/EditEnvironment/types'
import { AgentsSecretsHost, Warehouse } from 'types'
import { capitalize } from 'lodash'
import { object, string } from 'yup'
import { ClusterType } from 'api/createProjectForm/types/DatabricksComputeResourcesResponse'

export const editEnvironmentSchema = (projectType: Warehouse | '') => ({
  [PROJECTS_CREDENTIALS]: object({
    environmentName: string()
      .required('fields.environmentName.error.required')
      .matches(
        alphaNumericMildCharsWithLeadEndSpacesRegEx,
        'fields.environmentName.error.regEx'
      ),
    etlAgent: object({
      id: string().required('fields.etlAgent.error.required'),
      name: string().required('fields.etlAgent.error.required')
    })
      .when('projectConfiguration', {
        is: AgentsSecretsHost.MatillionHosted,
        then: (_existingSchema) => {
          return object({
            id: string().optional(),
            name: string().optional()
          })
        }
      })
      .required('fields.etlAgent.error.unmatched')
      .nullable()
  }).concat(warehouseValuesSchema(projectType, true).credentials),
  [PROJECTS_DEFAULTS]: warehouseValuesSchema(projectType).defaults
})

export const initialEditEnvironmentValues = (
  environmentFromResponse: GETEnvironmentsResponseParams,
  agentName: string,
  secretLocation: string
): EditEnvironmentFormikValueTypes => ({
  ...emptyInitialValues,
  environmentName: environmentFromResponse.name,
  etlAgent: { id: environmentFromResponse.agentId, name: agentName },
  matillionHostedAgentId: environmentFromResponse.agentId,
  projectConfiguration:
    agentName === 'Matillion Hosted Agent'
      ? AgentsSecretsHost.MatillionHosted
      : AgentsSecretsHost.CustomerHosted,
  account: environmentFromResponse.warehouseConnection.url,
  username: environmentFromResponse.warehouseConnection.username,
  secretName: {
    id: environmentFromResponse.warehouseConnection.secretName,
    name: environmentFromResponse.warehouseConnection.secretName
  },
  secretKey: {
    id: environmentFromResponse.warehouseConnection.secretKey,
    name: environmentFromResponse.warehouseConnection.secretKey
  },
  ...(environmentFromResponse.warehouseConnection?.defaultRole && {
    defaultRole: {
      id: environmentFromResponse.warehouseConnection.defaultRole,
      name: environmentFromResponse.warehouseConnection.defaultRole
    }
  }),
  ...(environmentFromResponse.warehouseConnection?.defaultDatabase && {
    defaultDatabase: {
      id: environmentFromResponse.warehouseConnection.defaultDatabase,
      name: environmentFromResponse.warehouseConnection.defaultDatabase
    }
  }),
  ...(environmentFromResponse.warehouseConnection?.defaultSchema && {
    defaultSchema: {
      id: environmentFromResponse.warehouseConnection.defaultSchema,
      name: environmentFromResponse.warehouseConnection.defaultSchema
    }
  }),
  ...(environmentFromResponse.warehouseConnection?.defaultWarehouse && {
    defaultWarehouse: {
      id: environmentFromResponse.warehouseConnection.defaultWarehouse,
      name: environmentFromResponse.warehouseConnection.defaultWarehouse
    }
  }),
  secretReferenceId: environmentFromResponse.warehouseConnection.secretId,
  secretLocationId: secretLocation,
  type: capitalize(
    environmentFromResponse.warehouseConnection.type
  ) as Warehouse,
  port: environmentFromResponse.warehouseConnection.port,
  ssl: environmentFromResponse.warehouseConnection.ssl,
  ...(environmentFromResponse.warehouseConnection?.clusterId &&
    environmentFromResponse.warehouseConnection?.clusterName &&
    environmentFromResponse.warehouseConnection?.clusterType && {
      compute: {
        id: environmentFromResponse.warehouseConnection.clusterId,
        name: environmentFromResponse.warehouseConnection.clusterName,
        clusterId: environmentFromResponse.warehouseConnection.clusterId,
        clusterName: environmentFromResponse.warehouseConnection.clusterName,
        clusterType: environmentFromResponse.warehouseConnection
          .clusterType as ClusterType
      }
    }),
  ...(environmentFromResponse.warehouseConnection?.defaultCatalog && {
    catalog: {
      id: environmentFromResponse.warehouseConnection.defaultCatalog,
      name: environmentFromResponse.warehouseConnection.defaultCatalog
    }
  })
})
